import React, { useState, useEffect } from "react";
import { Warehouse } from "../../types/Warehouse";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Modal from "../ui/Modal";

interface WarehouseFormProps {
  warehouse: Warehouse | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (warehouse: Warehouse) => void;
}

const WarehouseForm: React.FC<WarehouseFormProps> = ({
  warehouse,
  isOpen,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState<Partial<Warehouse>>({
    name: "",
    externalId: 0,
    notificationEmail: "",
    hasWarehouseLocations: false,
  });

  const [emails, setEmails] = useState<string[]>([""]);

  useEffect(() => {
    if (warehouse) {
      setForm(warehouse);
      // Split the notification emails into array if they exist
      if (warehouse.notificationEmail) {
        setEmails(
          warehouse.notificationEmail.split(",").map((email) => email.trim())
        );
      } else {
        setEmails([""]);
      }
    } else {
      setForm({
        name: "",
        externalId: 0,
        notificationEmail: "",
        hasWarehouseLocations: false,
      });
      setEmails([""]);
    }
  }, [warehouse]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setForm({
      ...form,
      [name]:
        type === "checkbox"
          ? checked
          : name === "externalId"
          ? parseInt(value) || 0
          : value,
    });
  };

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const addEmailField = () => {
    setEmails([...emails, ""]);
  };

  const removeEmailField = (index: number) => {
    if (emails.length > 1) {
      const newEmails = [...emails];
      newEmails.splice(index, 1);
      setEmails(newEmails);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Ensure all required fields are filled
    if (!form.name || !form.externalId) {
      return;
    }

    // Join all emails with commas
    const notificationEmail = emails
      .filter((email) => email.trim() !== "")
      .join(",");

    onSave({
      id: warehouse?.id,
      uuid: warehouse?.uuid,
      name: form.name || "",
      externalId: form.externalId || 0,
      notificationEmail: notificationEmail,
      hasWarehouseLocations: form.hasWarehouseLocations || false,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={warehouse ? "Modifica Magazzino" : "Aggiungi Magazzino"}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Nome"
          name="name"
          value={form.name || ""}
          onChange={handleChange}
          required
          fullWidth
        />
        <Input
          label="ID Esterno"
          name="externalId"
          type="number"
          value={form.externalId || ""}
          onChange={handleChange}
          required
          fullWidth
        />

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Email Notifiche
          </label>

          {emails.map((email, index) => (
            <div key={index} className="flex items-center space-x-2">
              <div className="flex-grow">
                <Input
                  name={`email-${index}`}
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(index, e.target.value)}
                  fullWidth
                  placeholder="esempio@dominio.it"
                />
              </div>
              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removeEmailField(index)}
                  className="p-2 mb-4 text-red-600 hover:text-red-800"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={addEmailField}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Aggiungi email
          </button>
        </div>

        <div className="flex items-center">
          <input
            id="hasWarehouseLocations"
            name="hasWarehouseLocations"
            type="checkbox"
            checked={form.hasWarehouseLocations || false}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label
            htmlFor="hasWarehouseLocations"
            className="ml-2 block text-sm text-gray-900"
          >
            Attiva locazioni magazzino
          </label>
        </div>
        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="secondary" onClick={onClose}>
            Annulla
          </Button>
          <Button type="submit">Salva</Button>
        </div>
      </form>
    </Modal>
  );
};

export default WarehouseForm;
